import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  X,
  Save,
  Plane,
  Clock,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api';
import { API_ROUTES, Role } from '@avgrp/shared';
import { useAuth } from '@/hooks/useAuth';
import { hasMinRole } from '@/utils/roles';

// ─── Types ──────────────────────────────────────────────────

type AircraftCategory = 'REGIONAL' | 'MEDIUM' | 'HEAVY';

interface AircraftType {
  id: string;
  icaoCode: string;
  iataCode: string | null;
  name: string;
  manufacturer: string | null;
  category: AircraftCategory;
  defaultPrepTime: number;
  createdAt: string;
  updatedAt: string;
  _count: { aircraft: number };
}

interface AircraftDetail {
  id: string;
  registration: string;
}

interface AircraftTypeDetail extends AircraftType {
  aircraft: AircraftDetail[];
}

interface AircraftTypeForm {
  icaoCode: string;
  iataCode: string;
  name: string;
  manufacturer: string;
  category: AircraftCategory;
  defaultPrepTime: number;
}

const emptyForm: AircraftTypeForm = {
  icaoCode: '',
  iataCode: '',
  name: '',
  manufacturer: '',
  category: 'MEDIUM',
  defaultPrepTime: 60,
};

const CATEGORY_STYLES: Record<AircraftCategory, { bg: string; text: string; label: string }> = {
  REGIONAL: { bg: 'bg-teal-50', text: 'text-teal-700', label: 'Regional' },
  MEDIUM: { bg: 'bg-indigo-50', text: 'text-indigo-700', label: 'Medium' },
  HEAVY: { bg: 'bg-orange-50', text: 'text-orange-700', label: 'Heavy' },
};

// ─── Component ──────────────────────────────────────────────

export function AircraftTypesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AircraftTypeForm>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // ─── Queries ────────────────────────────────────────────────
  const { data: types, isLoading } = useQuery({
    queryKey: ['aircraft-types'],
    queryFn: () =>
      apiGet<{ data: AircraftType[] }>(`${API_ROUTES.RESOURCES}/aircraft-types`),
    select: (res) => res.data,
  });

  // ─── Mutations ──────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (data: AircraftTypeForm) =>
      apiPost(`${API_ROUTES.RESOURCES}/aircraft-types`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aircraft-types'] });
      setShowCreateForm(false);
      setFormData(emptyForm);
      setFormErrors({});
    },
    onError: (err: any) => {
      setFormErrors({
        general: err?.response?.data?.error?.message || err?.message || 'Failed to create',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AircraftTypeForm> }) =>
      apiPut(`${API_ROUTES.RESOURCES}/aircraft-types/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aircraft-types'] });
      setEditingId(null);
      setFormData(emptyForm);
      setFormErrors({});
    },
    onError: (err: any) => {
      setFormErrors({
        general: err?.response?.data?.error?.message || err?.message || 'Failed to update',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`${API_ROUTES.RESOURCES}/aircraft-types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aircraft-types'] });
    },
  });

  // Query for expanded type detail (shows aircraft list)
  const { data: expandedDetail } = useQuery({
    queryKey: ['aircraft-type-detail', expandedId],
    queryFn: () =>
      apiGet<{ data: AircraftTypeDetail }>(`${API_ROUTES.RESOURCES}/aircraft-types/${expandedId}`),
    select: (res) => res.data,
    enabled: !!expandedId,
  });

  // Mutation to delete an individual aircraft from a type
  const deleteAircraftMutation = useMutation({
    mutationFn: (aircraftId: string) =>
      apiDelete(`${API_ROUTES.RESOURCES}/aircraft/${aircraftId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aircraft-types'] });
      queryClient.invalidateQueries({ queryKey: ['aircraft-type-detail'] });
    },
  });

  // ─── Helpers ────────────────────────────────────────────────
  const typesList = types ?? [];

  const filteredTypes = typesList.filter((t) => {
    if (categoryFilter && t.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        t.icaoCode.toLowerCase().includes(q) ||
        (t.iataCode?.toLowerCase() || '').includes(q) ||
        t.name.toLowerCase().includes(q) ||
        (t.manufacturer?.toLowerCase() || '').includes(q)
      );
    }
    return true;
  });

  const startEdit = (type: AircraftType) => {
    setEditingId(type.id);
    setFormData({
      icaoCode: type.icaoCode,
      iataCode: type.iataCode || '',
      name: type.name,
      manufacturer: type.manufacturer || '',
      category: type.category,
      defaultPrepTime: type.defaultPrepTime,
    });
    setFormErrors({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowCreateForm(false);
    setFormData(emptyForm);
    setFormErrors({});
  };

  const handleSubmit = () => {
    const errors: Record<string, string> = {};
    if (!formData.icaoCode.trim()) errors.icaoCode = 'Required';
    if (formData.icaoCode.length > 4) errors.icaoCode = 'Max 4 chars';
    if (!formData.name.trim()) errors.name = 'Required';
    if (formData.defaultPrepTime < 5) errors.defaultPrepTime = 'Min 5 min';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload: AircraftTypeForm = {
      ...formData,
      icaoCode: formData.icaoCode.toUpperCase(),
      iataCode: formData.iataCode?.toUpperCase() || '',
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aircraft Types</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage aircraft type definitions and default configurations
          </p>
        </div>
        {hasMinRole(user?.role, Role.MANAGER) && (
          <button
            onClick={() => {
              setShowCreateForm(true);
              setEditingId(null);
              setFormData(emptyForm);
              setFormErrors({});
            }}
            className="btn-primary"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Aircraft Type
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ICAO, IATA, name, manufacturer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {[
            { key: '', label: 'All' },
            { key: 'REGIONAL', label: 'Regional' },
            { key: 'MEDIUM', label: 'Medium' },
            { key: 'HEAVY', label: 'Heavy' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setCategoryFilter(key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                categoryFilter === key
                  ? 'bg-white text-aviation-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Create / Edit form */}
      {hasMinRole(user?.role, Role.MANAGER) && (showCreateForm || editingId) && (
        <div className="card border-aviation-200 bg-aviation-50/30">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              {editingId ? 'Edit Aircraft Type' : 'New Aircraft Type'}
            </h3>
            <button onClick={cancelEdit} className="rounded p-1 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          {formErrors.general && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {formErrors.general}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {/* ICAO Code */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                ICAO Code <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.icaoCode}
                onChange={(e) => setFormData({ ...formData, icaoCode: e.target.value.toUpperCase() })}
                placeholder="B738"
                maxLength={4}
                className={`input-field font-mono uppercase ${formErrors.icaoCode ? 'border-red-300' : ''}`}
              />
              {formErrors.icaoCode && <p className="mt-0.5 text-xs text-red-500">{formErrors.icaoCode}</p>}
            </div>

            {/* IATA Code */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">IATA Code</label>
              <input
                type="text"
                value={formData.iataCode}
                onChange={(e) => setFormData({ ...formData, iataCode: e.target.value.toUpperCase() })}
                placeholder="738"
                maxLength={3}
                className="input-field font-mono uppercase"
              />
            </div>

            {/* Name */}
            <div className="col-span-2 md:col-span-1 lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Boeing 737-800"
                className={`input-field ${formErrors.name ? 'border-red-300' : ''}`}
              />
              {formErrors.name && <p className="mt-0.5 text-xs text-red-500">{formErrors.name}</p>}
            </div>

            {/* Manufacturer */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Manufacturer</label>
              <input
                type="text"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                placeholder="Boeing"
                className="input-field"
              />
            </div>

            {/* Category */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Category <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as AircraftCategory })}
                className="input-field"
              >
                <option value="REGIONAL">Regional</option>
                <option value="MEDIUM">Medium</option>
                <option value="HEAVY">Heavy</option>
              </select>
            </div>

            {/* Default Prep Time */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Prep Time (min)
              </label>
              <input
                type="number"
                value={formData.defaultPrepTime}
                onChange={(e) => setFormData({ ...formData, defaultPrepTime: parseInt(e.target.value) || 60 })}
                min={5}
                max={300}
                className={`input-field ${formErrors.defaultPrepTime ? 'border-red-300' : ''}`}
              />
              {formErrors.defaultPrepTime && (
                <p className="mt-0.5 text-xs text-red-500">{formErrors.defaultPrepTime}</p>
              )}
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button onClick={cancelEdit} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isPending}
              className="btn-primary"
            >
              {isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
          </div>
        ) : filteredTypes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Plane className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm text-gray-500">
              {searchQuery || categoryFilter
                ? 'No aircraft types match your filter'
                : 'No aircraft types defined yet'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-header">ICAO</th>
                <th className="table-header">IATA</th>
                <th className="table-header">Name</th>
                <th className="table-header">Manufacturer</th>
                <th className="table-header">Category</th>
                <th className="table-header">Prep Time</th>
                <th className="table-header">Aircraft</th>
                <th className="table-header"></th>
              </tr>
            </thead>
            <tbody>
              {filteredTypes.map((type) => {
                const catStyle = CATEGORY_STYLES[type.category];
                const isExpanded = expandedId === type.id;

                return (
                  <React.Fragment key={type.id}>
                    <tr
                      className="cursor-pointer border-b border-gray-100 transition-colors last:border-0 hover:bg-gray-50"
                      onClick={() => setExpandedId(isExpanded ? null : type.id)}
                    >
                      <td className="table-cell font-mono font-bold text-gray-900">
                        {type.icaoCode}
                      </td>
                      <td className="table-cell font-mono text-gray-500">
                        {type.iataCode || '-'}
                      </td>
                      <td className="table-cell font-medium text-gray-800">
                        {type.name}
                      </td>
                      <td className="table-cell text-gray-500">
                        {type.manufacturer || '-'}
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${catStyle.bg} ${catStyle.text}`}>
                          {catStyle.label}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="h-3 w-3" />
                          {type.defaultPrepTime}m
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {type._count.aircraft}
                        </span>
                      </td>
                      <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {hasMinRole(user?.role, Role.MANAGER) && (
                            <>
                              <button
                                onClick={() => startEdit(type)}
                                className="rounded p-1 text-gray-400 hover:bg-aviation-50 hover:text-aviation-600"
                                title="Edit"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  if (type._count.aircraft > 0) {
                                    setExpandedId(type.id);
                                    return;
                                  }
                                  if (window.confirm(`Delete aircraft type ${type.icaoCode} (${type.name})?`)) {
                                    deleteMutation.mutate(type.id);
                                  }
                                }}
                                disabled={deleteMutation.isPending}
                                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                title={type._count.aircraft > 0 ? 'Remove aircraft first' : 'Delete'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded aircraft list */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="bg-gray-50 px-6 py-3">
                          <div className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Aircraft using {type.icaoCode}
                            {type._count.aircraft > 0 && (
                              <span className="ml-1 text-gray-400">
                                — remove all to enable type deletion
                              </span>
                            )}
                          </div>
                          {expandedDetail && expandedId === type.id ? (
                            expandedDetail.aircraft.length === 0 ? (
                              <p className="text-sm text-gray-400">No aircraft assigned to this type</p>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {expandedDetail.aircraft.map((ac) => (
                                  <div
                                    key={ac.id}
                                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5"
                                  >
                                    <Plane className="h-3 w-3 text-gray-400" />
                                    <span className="font-mono text-sm font-medium text-gray-700">
                                      {ac.registration}
                                    </span>
                                    {hasMinRole(user?.role, Role.MANAGER) && (
                                      <button
                                        onClick={() => {
                                          if (window.confirm(`Remove aircraft ${ac.registration} from this type? This will delete the aircraft record.`)) {
                                            deleteAircraftMutation.mutate(ac.id);
                                          }
                                        }}
                                        disabled={deleteAircraftMutation.isPending}
                                        className="rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-500"
                                        title={`Remove ${ac.registration}`}
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading aircraft...
                            </div>
                          )}
                          {deleteAircraftMutation.isError && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
                              <AlertCircle className="h-4 w-4" />
                              {(deleteAircraftMutation.error as any)?.response?.data?.error?.message
                                || 'Cannot delete: aircraft may have associated flights'}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6 text-sm text-gray-500">
        <span>
          {typesList.length} type{typesList.length !== 1 ? 's' : ''}
        </span>
        <span>
          {typesList.reduce((sum, t) => sum + t._count.aircraft, 0)} total aircraft
        </span>
        {['REGIONAL', 'MEDIUM', 'HEAVY'].map((cat) => {
          const count = typesList.filter((t) => t.category === cat).length;
          if (count === 0) return null;
          const style = CATEGORY_STYLES[cat as AircraftCategory];
          return (
            <span key={cat} className={`${style.text}`}>
              {count} {style.label}
            </span>
          );
        })}
      </div>

      {/* Delete error */}
      {deleteMutation.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {(deleteMutation.error as any)?.response?.data?.error?.message
            || (deleteMutation.error as any)?.message
            || 'Failed to delete'}
        </div>
      )}
    </div>
  );
}
